// Google OAuth popup flow — no external library needed. Works inside Telegram
// Mini Apps because it only uses window.open() and postMessage.

import { config } from '@/config';

const CALLBACK_PATH = '/auth/google-callback.html';
const SCOPES = 'openid email profile';

function randomNonce(): string {
  const arr = new Uint8Array(24);
  crypto.getRandomValues(arr);
  return Array.from(arr, b => b.toString(16).padStart(2, '0')).join('');
}

export function signInWithGoogle(): Promise<string> {
  const clientId = config.auth.googleClientId;
  if (!clientId || clientId.startsWith('YOUR_')) {
    return Promise.reject(new Error('Google Client ID is not configured. Set VITE_GOOGLE_CLIENT_ID in your .env file.'));
  }

  const origin = window.location.origin;
  const redirectUri = origin + CALLBACK_PATH;
  const nonce = randomNonce();

  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: redirectUri,
    response_type: 'id_token',
    scope: SCOPES,
    nonce,
    prompt: 'select_account',
  });

  const width = 500, height = 600;
  const left = (screen.width - width) / 2;
  const top = (screen.height - height) / 2;
  const popup = window.open(
    `https://accounts.google.com/o/oauth2/v2/auth?${params}`,
    'google-oauth',
    `width=${width},height=${height},left=${left},top=${top},popup=yes`,
  );

  if (!popup) {
    return Promise.reject(new Error('Popup was blocked. Please allow popups for this site.'));
  }

  return new Promise<string>((resolve, reject) => {
    const timeout = setTimeout(() => {
      cleanup();
      reject(new Error('Google sign-in timed out.'));
    }, 120_000);

    function onMessage(e: MessageEvent) {
      if (e.origin !== origin) return;
      if (e.data?.type !== 'google-auth') return;
      cleanup();
      if (e.data.idToken) {
        resolve(e.data.idToken);
      } else {
        reject(new Error(e.data.error ?? 'Google sign-in failed.'));
      }
    }

    function onUnload() {
      // Popup closed without posting back = user cancelled
      cleanup();
      reject(new Error('Google sign-in was cancelled.'));
    }

    function cleanup() {
      clearTimeout(timeout);
      window.removeEventListener('message', onMessage);
      popup?.removeEventListener?.('unload', onUnload);
      try { popup?.close(); } catch {}
    }

    window.addEventListener('message', onMessage);
    popup.addEventListener('unload', onUnload);
  });
}
